import React from 'react';
import { useTranslation } from 'react-i18next';
import { WarningIcon } from '@components-library/Icons/iconsComponents';
import { keyMap } from '@locales/lokalise/keyMap';
import CONSTANTS from 'components/shared/constants';
import { NotificationMessage } from 'components/shared/notification';
import { ENUM_LOCALE_CODE } from 'global.enums';
import { IBrokerParams, IUser } from 'global.interfaces';
import i18next from 'i18next';
import { i18n } from 'i18next';
import { PspOperationsNotification } from '../psp/pspOperationsNotification';

interface UserStatusData {
  isUserApproved: boolean;
  depositAllowedForUnverifiedProfile: boolean;
  maxTotalDepositAmountForUnverifiedProfile: number;
  userCanDeposit: boolean;
  userCanWithdraw: boolean;
  userIsNotApprovedAndCanDeposit: boolean;
  hasAnyActiveKycChecks: boolean;
}

export const getUserStatusData = (userInfo?: IUser | null): UserStatusData => {
  if (!userInfo) {
    return getEmptyData();
  }

  const {
    isUserApproved,
    depositAllowedForUnverifiedProfile,
    maxTotalDepositAmountForUnverifiedProfile,
    hasAnyActiveKycChecks,
  } = userInfo;
  const userCanDeposit = isUserApproved || depositAllowedForUnverifiedProfile;
  const userCanWithdraw = isUserApproved;
  const userIsNotApprovedAndCanDeposit = !isUserApproved && depositAllowedForUnverifiedProfile;

  return {
    isUserApproved,
    depositAllowedForUnverifiedProfile,
    maxTotalDepositAmountForUnverifiedProfile,
    userCanDeposit,
    userCanWithdraw,
    userIsNotApprovedAndCanDeposit,
    hasAnyActiveKycChecks,
  };
};

const getEmptyData = (): UserStatusData => {
  return {
    isUserApproved: false,
    depositAllowedForUnverifiedProfile: false,
    maxTotalDepositAmountForUnverifiedProfile: 0,
    userCanDeposit: false,
    userCanWithdraw: false,
    userIsNotApprovedAndCanDeposit: false,
    hasAnyActiveKycChecks: false,
  };
};

interface DepositWarningMessageProps {
  userInfo: IUser;
  depositTabLocation: boolean;
  brokerParams: IBrokerParams;
}

export const DepositWarningMessage = (props: DepositWarningMessageProps) => {
  const { i18n } = useTranslation();
  const userStatusData = getUserStatusData(props.userInfo);
  const showDepositWarningMessage = props.depositTabLocation && userStatusData.userCanDeposit;

  if (!showDepositWarningMessage) {
    return null;
  }

  const { depositWarningTitle, depositWarningMessageList } = getDepositWarningMessages(props.brokerParams, i18n);

  return (
    <NotificationMessage
      title={depositWarningTitle}
      listItems={depositWarningMessageList}
      useRollUp={true}
      type={CONSTANTS.NOTIFICATION_TYPE.WARNING}
      icon={<WarningIcon size="inherit" />}
    />
  );
};

const getDepositWarningMessages = (brokerParams: IBrokerParams, i18n: i18n) => {
  const currentLanguage = i18n.language as ENUM_LOCALE_CODE;

  const depositWarningTitle = keyMap.profileWarningMessageImportantInformation;
  const brokerName = brokerParams[currentLanguage]?.brokerName;
  const depositWarningMessageList = [
    i18n.t(keyMap.profileWarningMessageThirdPartyDeposits, { brokerName }),
    i18n.t(keyMap.profileWarningMessageConfirmThatPaymentsAreMadeFromRegisteredAccount, { brokerName }),
    i18n.t(keyMap.profileWarningMessageNameDoesNotMatch),
    i18n.t(keyMap.profileWarningMessageVerifyYourIdentity, { brokerName }),
    ...(currentLanguage === ENUM_LOCALE_CODE.zhCN
      ? [i18n.t(keyMap.profileWarningMessageMakeSureDescriptionIsEmptyForUnionPayOnlyCN)]
      : []),
  ];

  return {
    depositWarningTitle,
    depositWarningMessageList,
  };
};

interface NeitherDepositNorWithdrawAllowedMessageProps {
  userInfo: IUser;
  depositTabLocation: boolean;
  withdrawalTabLocation: boolean;
}

export const NeitherDepositNorWithdrawAllowedMessage = (props: NeitherDepositNorWithdrawAllowedMessageProps) => {
  const userStatusData = getUserStatusData(props.userInfo);
  const showNeitherDepositNorWithdrawAllowedMessage =
    !userStatusData.userCanDeposit &&
    !userStatusData.userCanWithdraw &&
    (props.depositTabLocation || props.withdrawalTabLocation);

  if (!showNeitherDepositNorWithdrawAllowedMessage) {
    return null;
  }

  return <PspOperationsNotification text={i18next.t(keyMap.profileDepositsAndWithdrawalOnlyForVerifiedAccounts)} />;
};

interface DepositLimitMessageProps {
  userInfo: IUser;
  depositTabLocation: boolean;
}

export const DepositLimitMessage = (props: DepositLimitMessageProps) => {
  const userStatusData = getUserStatusData(props.userInfo);
  const showDepositLimitMessage = props.depositTabLocation && userStatusData.userIsNotApprovedAndCanDeposit;
  const { maxTotalDepositAmountForUnverifiedProfile } = userStatusData;

  if (!showDepositLimitMessage) {
    return null;
  }

  return (
    <PspOperationsNotification
      text={i18next.t(keyMap.profileDepositsForUnverifiedAccounts, {
        maxAmount: maxTotalDepositAmountForUnverifiedProfile,
      })}
      depositTabLocation
    />
  );
};

interface WithdrawNotAllowedMessageProps {
  userInfo: IUser;
  withdrawalTabLocation: boolean;
}

export const WithdrawNotAllowedMessage = (props: WithdrawNotAllowedMessageProps) => {
  const userStatusData = getUserStatusData(props.userInfo);
  const showWithdrawNotAllowedMessage = props.withdrawalTabLocation && userStatusData.userIsNotApprovedAndCanDeposit;

  if (!showWithdrawNotAllowedMessage) {
    return null;
  }

  return <PspOperationsNotification text={i18next.t(keyMap.profileWithdrawalOnlyForVerifiedAccounts)} />;
};

type PspInfoMessagesProps = NeitherDepositNorWithdrawAllowedMessageProps;

export const PspInfoMessages = (props: PspInfoMessagesProps) => {
  const { userInfo, depositTabLocation, withdrawalTabLocation } = props;

  return (
    <React.Fragment>
      <NeitherDepositNorWithdrawAllowedMessage
        userInfo={userInfo}
        depositTabLocation={depositTabLocation}
        withdrawalTabLocation={withdrawalTabLocation}
      />

      <DepositLimitMessage userInfo={userInfo} depositTabLocation={depositTabLocation} />

      <WithdrawNotAllowedMessage userInfo={userInfo} withdrawalTabLocation={withdrawalTabLocation} />
    </React.Fragment>
  );
};
